import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { ACTIONS, AREAS, LEVELS, type Action, type Area, type Level, type PermMatrix, usePerms } from '@/lib/perms';
import { STAFF_ROLES, useRoleLabels } from './roles';

/** Настройки → «Права ролей» (29.10.2026): матрица роль × раздел (нет / чтение / изменение) и отдельные действия.
 * Колонка admin заблокирована — у администратора всегда всё. Сохраняет только admin (PUT /settings role_permissions);
 * остальные сотрудники видят матрицу для справки. Проверка прав — на сервере и в RLS, здесь только управление. */
type Catalog = { roles: string[]; areas: Area[]; actions: Action[]; levels: Level[]; matrix: PermMatrix; defaults: PermMatrix };

export default function RolesTab({ onToast }: { onToast: (msg: string, detail?: string, tone?: 'success' | 'error') => void }) {
  const { t } = useTranslation();
  const { refetch } = useAuth();
  const { admin } = usePerms();
  const roleLabel = useRoleLabels();
  const [cat, setCat] = useState<Catalog | null>(null);
  const [matrix, setMatrix] = useState<PermMatrix | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const c = await api.get<Catalog>('/settings/permissions');
      setCat(c);
      setMatrix(c.matrix);
    } catch (e) {
      setError((e as ApiError).code);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const dirty = useMemo(() => Boolean(cat && matrix && JSON.stringify(cat.matrix) !== JSON.stringify(matrix)), [cat, matrix]);
  const editable = STAFF_ROLES.filter((r) => r !== 'admin');

  const setLevel = (role: string, area: Area, level: Level) =>
    setMatrix((m) => (m ? { ...m, [role]: { ...m[role], areas: { ...m[role].areas, [area]: level } } } : m));
  const setAction = (role: string, action: Action, v: boolean) =>
    setMatrix((m) => (m ? { ...m, [role]: { ...m[role], actions: { ...m[role].actions, [action]: v } } } : m));

  const save = async () => {
    if (!matrix) return;
    setBusy(true);
    try {
      await api.put('/settings', { values: { role_permissions: matrix } });
      await load();
      await refetch(); // свои права тоже могли измениться (меню/кнопки)
      onToast(t('perm.saved'));
    } catch (e) {
      onToast(t('ui.error'), (e as ApiError).code, 'error');
    } finally {
      setBusy(false);
    }
  };

  const levelTone: Record<Level, string> = {
    none: 'border-background-200 bg-background-100 text-foreground-500',
    read: 'border-secondary-200 bg-secondary-50 text-secondary-800',
    write: 'border-primary-200 bg-primary-50 text-primary-800',
  };
  const selectCls = (lvl: Level) => `h-7 w-full cursor-pointer rounded border px-1.5 text-[11px] font-medium outline-none focus:border-primary-400 disabled:cursor-default ${levelTone[lvl]}`;

  if (error) return <p className="mt-5 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700">{t('onb.error', { code: error })}</p>;
  if (!cat || !matrix) return <p className="mt-5 text-[12px] text-foreground-500">{t('ui.loading')}</p>;

  return (
    <div className="mt-5 flex flex-col gap-4">
      <div className="rounded-lg border border-background-200 bg-background-50 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-foreground-950">{t('perm.title')}</h2>
            <p className="mt-0.5 max-w-[720px] text-[11.5px] leading-4 text-foreground-500">{t('perm.sub')}</p>
          </div>
          {admin && (
            <div className="flex shrink-0 items-center gap-2">
              <button type="button" disabled={busy || !dirty} onClick={() => setMatrix(cat.defaults)} className="h-8 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-3 text-[12px] font-medium text-foreground-700 hover:bg-background-100 disabled:cursor-default disabled:opacity-50">
                {t('perm.reset')}
              </button>
              <button type="button" disabled={busy || !dirty} onClick={() => void save()} className="inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 hover:bg-primary-600 disabled:cursor-default disabled:opacity-50">
                <i className="ri-save-line text-[14px]" /> {busy ? t('ui.saving') : t('perm.save')}
              </button>
            </div>
          )}
        </div>
        {!admin && <p className="mt-3 rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-[11.5px] text-accent-900">{t('perm.readOnlyNote')}</p>}

        <div className="mt-4 overflow-x-auto thin-scroll">
          <table className="w-full min-w-[760px] border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-background-200 text-left text-[10.5px] uppercase tracking-[0.08em] text-foreground-500">
                <th className="py-2 pr-3 font-medium">{t('perm.colArea')}</th>
                <th className="px-2 py-2 font-medium">
                  {roleLabel.admin} <i className="ri-lock-line text-[11px]" title={t('perm.adminLocked')} />
                </th>
                {editable.map((r) => (
                  <th key={r} className="px-2 py-2 font-medium">{roleLabel[r]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {AREAS.map((area) => (
                <tr key={area} className="border-b border-background-200/70 last:border-0">
                  <td className="py-2 pr-3">
                    <p className="font-medium text-foreground-900">{t(`perm.area.${area}`)}</p>
                    <p className="text-[10.5px] leading-4 text-foreground-400">{t(`perm.areaHint.${area}`)}</p>
                  </td>
                  <td className="px-2 py-2">
                    <span className={`inline-flex h-7 items-center rounded border px-2 text-[11px] font-medium ${levelTone.write}`}>{t('perm.level.write')}</span>
                  </td>
                  {editable.map((r) => {
                    const lvl = matrix[r]?.areas[area] ?? 'none';
                    return (
                      <td key={r} className="px-2 py-2">
                        <select value={lvl} disabled={!admin} onChange={(e) => setLevel(r, area, e.target.value as Level)} aria-label={`${roleLabel[r]} · ${t(`perm.area.${area}`)}`} className={selectCls(lvl)}>
                          {LEVELS.map((l) => (
                            <option key={l} value={l}>
                              {t(`perm.level.${l}`)}
                            </option>
                          ))}
                        </select>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-4">
        <h2 className="text-sm font-semibold text-foreground-950">{t('perm.actionsTitle')}</h2>
        <p className="mt-0.5 max-w-[720px] text-[11.5px] leading-4 text-foreground-500">{t('perm.actionsSub')}</p>
        <div className="mt-3 overflow-x-auto thin-scroll">
          <table className="w-full min-w-[760px] border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-background-200 text-left text-[10.5px] uppercase tracking-[0.08em] text-foreground-500">
                <th className="py-2 pr-3 font-medium">{t('perm.colAction')}</th>
                <th className="px-2 py-2 font-medium">{roleLabel.admin}</th>
                {editable.map((r) => (
                  <th key={r} className="px-2 py-2 font-medium">{roleLabel[r]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ACTIONS.map((a) => (
                <tr key={a} className="border-b border-background-200/70 last:border-0">
                  <td className="py-2 pr-3">
                    <p className="font-medium text-foreground-900">{t(`perm.action.${a}`)}</p>
                    <p className="text-[10.5px] leading-4 text-foreground-400">{t(`perm.actionHint.${a}`)}</p>
                  </td>
                  <td className="px-2 py-2"><i className="ri-checkbox-circle-fill text-[15px] text-primary-600" /></td>
                  {editable.map((r) => (
                    <td key={r} className="px-2 py-2">
                      <input type="checkbox" checked={Boolean(matrix[r]?.actions[a])} disabled={!admin} onChange={(e) => setAction(r, a, e.target.checked)} aria-label={`${roleLabel[r]} · ${t(`perm.action.${a}`)}`} className="h-4 w-4 cursor-pointer accent-primary-600 disabled:cursor-default" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-100 p-4 text-[11.5px] leading-5 text-foreground-600">
        <p className="font-semibold text-foreground-900">{t('perm.howTitle')}</p>
        <ol className="mt-1 list-decimal pl-4">
          <li>{t('perm.how1')}</li>
          <li>{t('perm.how2')}</li>
          <li>{t('perm.how3')}</li>
        </ol>
      </div>
    </div>
  );
}
